import { formatCurrency, accountingSources } from "../../../helpers";
import styles from "./styles.module.scss";
import TransactionsTable from "./TransactionsTable";
import secondaryComponents from "../../secondaryComponents";
import primaryComponents from "../../primaryComponents";
import useChartAccountDetails from "./useChartAccountDetails";
import { useSelector } from "react-redux";
import { useStoreCurrency } from "../../../hooks/useStoreCurrency";
import FundsMovement from "../../../components/secondaryComponents/FundsMovement";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const ChartAccountDetails = ({
  chartAccountDetails,
  refetchChartAccountDetails = () => {},
}) => {
  const {
    storeList,
    currentStore,
    startDate,
    endDate,
    chartAccountTransactions,
    metaDetails,
    isLoadingChartAccountTransactions,
    showDepositCash,
    showWithdrawCash,
    showTransferCash,
    showExportAccountStatement,
    entrySource,
    handleFetchChartAccountTransactions,
    gotoPage,
    setDateFilter,
    filterByStoreId,
    setShowDepositCash,
    setShowWithdrawCash,
    setShowTransferCash,
    setShowExportAccountStatement,
    setEntrySourceFilter,
  } = useChartAccountDetails({
    account_id: chartAccountDetails?.id,
  });

  const { storeHasMultipleCurrency } = useStoreCurrency();
  const { currency_code, allow_old_accounting } = useSelector(
    (state) => state.profileDetailsReducer.businessDetails,
  );

  const netBalance = chartAccountDetails?.net_balance || 0;

  return (
    <div className={styles.chartAccountDetails}>
      <div className={styles.accountDetails}>
        <div className={styles.accountDetails__accountName}>
          <span>Account</span>
          <span>{chartAccountDetails?.account_name}</span>
          <span>
            {chartAccountDetails?.account_code}
            {chartAccountDetails?.account_type_label
              ? ` - ${chartAccountDetails?.account_type_label}`
              : ""}
            {chartAccountDetails?.bank_name
              ? ` - ${chartAccountDetails?.bank_name}`
              : ""}
          </span>
        </div>
        <div className={styles.accountDetails__balance}>
          <span>Total Debit</span>
          <span>
            {formatCurrency({
              value: chartAccountDetails?.total_debits || 0,
              currencyCode: currency_code,
            })}
          </span>
        </div>
        <div className={styles.accountDetails__balance}>
          <span>Total Credit</span>
          <span>
            {formatCurrency({
              value: chartAccountDetails?.total_credits || 0,
              currencyCode: currency_code,
            })}
          </span>
        </div>
        <div className={styles.accountDetails__balance}>
          <span>Available Balance</span>
          <span className="text-primary">
            {formatCurrency({
              value: Math.abs(netBalance),
              currencyCode: currency_code,
            })}
            {netBalance < 0 ? " Dr" : netBalance > 0 ? " Cr" : ""}
          </span>
        </div>
      </div>

      {chartAccountDetails?.description ? (
        <p className={`default-text-size ${styles.description}`}>
          {chartAccountDetails?.description}
        </p>
      ) : null}

      <div className={`${styles.card} mt-3`}>
        <div className="d-flex flex-wrap" style={{ gap: "5px" }}>
          <primaryComponents.DateDefinedRangeInput
            startDate={startDate}
            endDate={endDate}
            onChange={setDateFilter}
            showPlaceholder={true}
          />
          <div style={{ width: "200px" }}>
            <primaryComponents.SelectField
              options={
                storeHasMultipleCurrency
                  ? [...storeList]
                  : [{ name: "All stores", id: "ALL_STORES" }, ...storeList]
              }
              idKey="id"
              nameKey="name"
              onChange={(value) => {
                filterByStoreId(value);
              }}
              value={
                storeList?.find(
                  (storeItem) => `${storeItem?.id}` === `${currentStore}`,
                )?.name || "All stores"
              }
              floatError={false}
              classNames="store-select"
            />
          </div>
          <div className="ml-auto" style={{ width: "200px" }}>
            <primaryComponents.SelectField
              options={accountingSources}
              idKey="id"
              nameKey="name"
              onChange={(value) => {
                setEntrySourceFilter(value);
              }}
              value={
                accountingSources?.find(
                  (accountingSource) =>
                    `${accountingSource?.id}` === `${entrySource}`,
                )?.name || "All entry types"
              }
              floatError={false}
            />
          </div>
        </div>
        <div className="d-flex align-items-center" style={{ gap: "8px" }}>
          {/* Deposit from cash on hand to bank account */}
          {allow_old_accounting &&
          chartAccountDetails?.sub_account_type === "cash_and_bank" &&
          chartAccountDetails?.is_bank_account === false ? (
            <primaryComponents.Button
              classNames="btn btn--primary"
              onClick={() => setShowDepositCash(true)}
            >
              Deposit Cash
            </primaryComponents.Button>
          ) : null}
          {/* Withdraw from bank account to cash on hand */}
          {allow_old_accounting &&
          chartAccountDetails?.sub_account_type === "cash_and_bank" &&
          chartAccountDetails?.is_bank_account === true ? (
            <>
              <primaryComponents.Button
                classNames="btn btn--primary"
                onClick={() => setShowWithdrawCash(true)}
              >
                Withdraw Cash
              </primaryComponents.Button>
              <primaryComponents.Button
                classNames="btn btn--outline"
                onClick={() => setShowTransferCash(true)}
              >
                Bank Transfer
              </primaryComponents.Button>
            </>
          ) : null}
          <div>
            <primaryComponents.Button
              classNames="btn btn--outline"
              onClick={() => setShowExportAccountStatement(true)}
            >
              <FontAwesomeIcon icon="download" className="mr-2" />
              Export
            </primaryComponents.Button>
          </div>
        </div>
      </div>

      <div className={styles.tableBody}>
        <secondaryComponents.LoaderHelper
          isLoading={isLoadingChartAccountTransactions}
          classNames="mt-5"
        >
          <TransactionsTable
            transactions={chartAccountTransactions}
            handleFetchTransactions={handleFetchChartAccountTransactions}
            refetchChartAccountDetails={refetchChartAccountDetails}
            gotoPage={gotoPage}
            metaDetails={metaDetails}
          />
        </secondaryComponents.LoaderHelper>
      </div>
      <secondaryComponents.SideModal
        isActive={showDepositCash}
        closeModal={() => setShowDepositCash(false)}
        width={550}
      >
        <FundsMovement
          closeWidget={() => setShowDepositCash(false)}
          title="Cash Deposit"
          description="Deposit cash from this account to the bank"
          type={"deposit"}
          mainChartAccount={chartAccountDetails}
          handleFetchBankTransaction={handleFetchChartAccountTransactions}
          refetchChartAccountDetails={refetchChartAccountDetails}
        />
      </secondaryComponents.SideModal>
      <secondaryComponents.SideModal
        isActive={showWithdrawCash}
        closeModal={() => setShowWithdrawCash(false)}
        width={550}
      >
        <FundsMovement
          closeWidget={() => setShowWithdrawCash(false)}
          title="Cash Withdrawal"
          description="Withdraw cash from this account to the cash on hand account"
          type={"withdraw"}
          mainChartAccount={chartAccountDetails}
          handleFetchBankTransaction={handleFetchChartAccountTransactions}
          refetchChartAccountDetails={refetchChartAccountDetails}
        />
      </secondaryComponents.SideModal>
      <secondaryComponents.SideModal
        isActive={showTransferCash}
        closeModal={() => setShowTransferCash(false)}
        width={550}
      >
        <FundsMovement
          closeWidget={() => setShowTransferCash(false)}
          title="Bank Transfer"
          description="Transfer fund from this account to another bank account"
          type={"transfer"}
          mainChartAccount={chartAccountDetails}
          handleFetchBankTransaction={handleFetchChartAccountTransactions}
          refetchChartAccountDetails={refetchChartAccountDetails}
        />
      </secondaryComponents.SideModal>
      <secondaryComponents.SideModal
        isActive={showExportAccountStatement}
        closeModal={() => setShowExportAccountStatement(false)}
      >
        <secondaryComponents.AccountStatementExport
          closeWidget={() => setShowExportAccountStatement(false)}
          chartAccountId={chartAccountDetails?.id}
        />
      </secondaryComponents.SideModal>
    </div>
  );
};

export default ChartAccountDetails;
